import Button from 'Components/nui/Button';
import { FormattedMessage } from 'react-intl';
import FilterDropdown from './FilterDropdown';
import { FilterTypes, useFilters } from './context';
import FilterChips from './FilterChips';
import { useStoreState } from 'easy-peasy';
import { DivisionWarehouseFilter } from 'Pages/Market/DivisionWarehouse';
import { PresetForm, PresetsList } from 'Pages/Market/Filter';
import { useFilterOptions } from '~/hooks/useFilterOptions';
import { Link, useRouteMatch } from 'react-router-dom';
import HelpButton from 'Pages/Market/HelpButton';
import * as R from 'ramda';

const MarketFilters = () => {
  const solutionSettings = useStoreState(
    (state) => state.auth.solutionSettings
  );
  const overviewAllowed = R.pathOr(true, ['overview'], solutionSettings);
  const match = useRouteMatch('/overview');
  const currentView = match ? 'overview' : 'marketplace';

  const { setFilters } = useFilters();

  const {
    productOptions,
    groupOptions,
    typeOptions,
    etdOptions,
    publishedDateOptions,
    stateOptions,
    originOptions,
  } = useFilterOptions();

  const isOverview = currentView === 'overview';

  return (
    <div className="filter-row tw:mx-4 tw:-mt-5">
      <FilterChips
        trailing={
          <>
            {!isOverview && <HelpButton />}
            {overviewAllowed && <ToggleNav currentView={currentView} />}
          </>
        }
      >
        {!isOverview && (
          <>
            {solutionSettings?.matchinglevel !== 'commodity' && (
              <DivisionWarehouseFilter />
            )}
            <FilterDropdown
              id="product"
              label={
                <FormattedMessage
                  id="marketplace-filter-field-products"
                  description="Label for 'Products' in Marketplace filter"
                  defaultMessage="Products"
                />
              }
              type={FilterTypes.CHECKBOXES}
              options={productOptions}
            />
            {groupOptions.length > 0 && (
              <FilterDropdown
                id="group"
                label={
                  <FormattedMessage
                    id="marketplace-filter-field-product-groups"
                    description="label for 'Product groups' in Marketplace filter"
                    defaultMessage="Product groups"
                  />
                }
                type={FilterTypes.CHECKBOXES}
                options={groupOptions}
              />
            )}
            <FilterDropdown
              id="type"
              label={
                <FormattedMessage
                  id="marketplace-filter-field-type"
                  description="Label for 'Type' in Marketplace filter"
                  defaultMessage="Type"
                />
              }
              type={FilterTypes.CHECKBOXES}
              options={typeOptions}
            />
            <FilterDropdown
              id="etdOption"
              label={
                <FormattedMessage
                  id="marketplace-filter-field-etd-short"
                  description="label for 'ETD' in Marketplace filter (short)"
                  defaultMessage="ETD"
                />
              }
              type={FilterTypes.RADIO}
              options={etdOptions}
              subFieldId="etd"
              subFieldType={FilterTypes.DATE}
            />
            <FilterDropdown
              id="updatedOption"
              label={
                <FormattedMessage
                  id="marketplace-filter-field-published"
                  description="label for 'Published' in Marketplace filter"
                  defaultMessage="Published"
                />
              }
              type={FilterTypes.RADIO}
              options={publishedDateOptions}
              subFieldId="updated"
              subFieldType={FilterTypes.DATE}
            />
            {stateOptions.length > 0 && (
              <FilterDropdown
                id="state"
                label={
                  <FormattedMessage
                    id="marketplace-filter-field-state"
                    description="label for 'State' in Marketplace filter"
                    defaultMessage="State"
                  />
                }
                type={FilterTypes.CHECKBOXES}
                options={stateOptions}
              />
            )}
            {originOptions.length > 0 && (
              <FilterDropdown
                id="origin"
                label={
                  <FormattedMessage
                    id="marketplace-filter-field-origin"
                    description="label for 'Origin' in Marketplace filter"
                    defaultMessage="Origin"
                  />
                }
                type={FilterTypes.CHECKBOXES}
                options={originOptions}
              />
            )}
            <Button
              type="buttonlink"
              className="filter-reset tw:mr-3"
              onClick={() => setFilters({})}
            >
              <FormattedMessage
                id="filter-button-clear-filter"
                description="Button for clear search filters"
                defaultMessage="Clear all filters"
              />
            </Button>
            <PresetForm />
            <PresetsList />
          </>
        )}
      </FilterChips>
    </div>
  );
};

const baseClass =
  'tw:flex-1 tw:rounded-full tw:px-4 tw:py-1 tw:text-center tw:text-xs tw:leading-3 tw:no-underline';
const activeClass = 'tw:bg-white tw:text-[var(--link)]';
const inactiveClass = 'tw:text-white tw:hover:text-white';

const ToggleNav = ({ currentView }: { currentView: string }) => (
  <div className="tw tw:flex tw:rounded-full tw:bg-[var(--link)] tw:p-0.5 tw:w-[180px]">
    <Link
      to="/"
      className={`${baseClass} ${currentView === 'marketplace' ? activeClass : inactiveClass}`}
    >
      <FormattedMessage
        id="marketplace-headertab-marketplace"
        defaultMessage="List"
      />
    </Link>
    <Link
      to="/overview"
      className={`${baseClass} ${currentView === 'overview' ? activeClass : inactiveClass}`}
    >
      <FormattedMessage
        id="marketplace-headertab-overview"
        defaultMessage="Overview"
      />
    </Link>
  </div>
);

export default MarketFilters;
